import { DropZone } from '@components/Files/DropZone';
import { FilePreview } from '@components/Files/FilePreview';
import { UploadProvider, useUploadContext } from '@components/Files/UploadProvider';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { IconButtonRegular } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import DiagonalArrowIcon from '@components/icons/DiagonalArrow.svg';
import LoadingIcon from '@components/icons/Loading.svg';
import StatusErrorIcon from '@components/icons/StatusError.svg';
import { AttachmentUploader } from '@pages/User/NewJobPage/helpers/attachmentUploader';
import { handleDownloadError } from '@helpers/jobLinkHelpers';
import { downloadFromUrl } from '@helpers/downloadFromUrl';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { JobAttachmentResponseDto, JobsService } from 'app/API';
import { createFetchNoticeExtractionStatusQueryFn } from 'app/queries/noticeExtractor/createFetchNoticeExtractionStatusQueryFn';
import { noticeExtractorKeys } from 'app/queries/noticeExtractor/noticeExtractorKeys';
import { EACCEPTED_DOCUMENT_FORMATS } from 'app/types/enums/EACCEPTED_DOCUMENT_FORMATS';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { getNoticeExtractionFailureMessage } from './helpers/getNoticeExtractionFailureMessage';
import { noticeExtractionFailedStates } from './helpers/resolveNoticeExtractionPollOutcome';
import { runNoticeExtractionPollTick, TNoticeExtractionPollState } from './helpers/runNoticeExtractionPollTick';

type TAttachmentType = 'GENERAL' | 'NOTICE';

type TAttachment = {
  id?: string;
  fileName: string;
  path: string;
  fileSize?: number;
  extracted?: boolean;
  attachmentType?: TAttachmentType | 'REPORTER_NOTES';
};

type TPendingNoticeFile = {
  id: string;
  name: string;
  isPdf: boolean;
};

const getFileSignature = (file: { name?: string; size?: number; lastModified?: number }) => {
  return `${file.name ?? ''}:${file.size ?? 0}:${file.lastModified ?? 0}`;
};

const isPdfFileMeta = (meta: { name?: string; type?: string }) => {
  return meta.type === 'application/pdf' || /\.pdf$/i.test(meta.name ?? '');
};

const getNoticeExtractionToastId = (attachmentId: string) => `notice-extraction-${attachmentId}`;

const NOTICE_EXTRACTION_POLL_INTERVAL_MS = 5000;

export type TProps = {
  accept?: string;
  multiple?: boolean;
  maxSizeBytes?: number;
  minSizeBytes?: number;
  className?: string;
  organizationId: string;
  organizationSlug: string;
  workspaceSlug: string;
  noticeExtractorEnabled: boolean;
  onNoticeExtractionRunningChange: (running: boolean) => void;
  onAttachmentUploadRunningChange: (running: boolean) => void;
  attachments: TAttachment[];
  ensureDraftJobBeforeUpload: () => Promise<string | null>;
  getJobId: () => string;
  onRefreshAttachment: (attachmentId: string) => Promise<JobAttachmentResponseDto | undefined>;
  onAttachmentChange: (attachment: JobAttachmentResponseDto) => void;
};

const UploadAttachmentsBlockInner: React.FC<{
  organizationId: string;
  organizationSlug: string;
  workspaceSlug: string;
  noticeExtractorEnabled: boolean;
  onNoticeExtractionRunningChange: (running: boolean) => void;
  onAttachmentUploadRunningChange: (running: boolean) => void;
  attachments: TAttachment[];
  ensureDraftJobBeforeUpload: () => Promise<string | null>;
  jobIdRef: React.MutableRefObject<string>;
  onRefreshAttachment: (attachmentId: string) => Promise<JobAttachmentResponseDto | undefined>;
  onAttachmentChange: (attachment: JobAttachmentResponseDto) => void;
}> = ({
  organizationId,
  organizationSlug,
  workspaceSlug,
  noticeExtractorEnabled,
  onNoticeExtractionRunningChange,
  onAttachmentUploadRunningChange,
  attachments,
  ensureDraftJobBeforeUpload,
  jobIdRef,
  onRefreshAttachment,
  onAttachmentChange,
}) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { files, setExtra, attachExisting, updateMeta } = useUploadContext();
  const removedAttachmentPathsRef = useRef(new Set<string>());
  const noticeExtractionPollsRef = useRef<Map<string, number>>(new Map());
  const noticeExtractionPollStatesRef = useRef<Map<string, TNoticeExtractionPollState>>(new Map());
  const noticeExtractionToastIdsRef = useRef<Set<string>>(new Set());
  const noticeExtractionTerminalToastEligibleRef = useRef<Set<string>>(new Set());
  const pendingNoticeSelectionsRef = useRef<Record<string, TAttachmentType>>({});
  const attachmentUploadRunningRef = useRef(false);
  const noticeExtractionRunningRef = useRef(false);
  const [noticeModalFiles, setNoticeModalFiles] = useState<TPendingNoticeFile[]>([]);
  const [noticeSelections, setNoticeSelections] = useState<Record<string, boolean>>({});

  const { mutateAsync: registerAttachment } = useMutation({
    mutationFn: (request: Parameters<typeof JobsService.putAttachment>[0]) =>
      call(() => JobsService.putAttachment(request)),
  });

  const { mutateAsync: deleteAttachment } = useMutation({
    mutationFn: (request: Parameters<typeof JobsService.deleteAttachment>[0]) =>
      call(() => JobsService.deleteAttachment(request)),
  });

  useEffect(() => {
    const existingRemoteIds = new Set(
      files
        .filter((file) => file.meta.status !== 'removed')
        .filter((file) => file.meta.status === 'external' || Boolean(file.meta.extra?.attachmentPath))
        .map((file) => String(file.meta.extra?.attachmentPath || file.meta.name)),
    );
    const notAttachedYet = attachments
      .filter(
        (attachment) =>
          !existingRemoteIds.has(attachment.path) && !removedAttachmentPathsRef.current.has(attachment.path),
      )
      .map((attachment) => ({
        meta: {
          id: attachment.path,
          name: attachment.fileName,
          size: attachment.fileSize,
          status: 'external' as const,
          percent: 100,
          uploadedAt: new Date().toISOString(),
          extra: {
            attachmentPath: attachment.path,
            attachmentType: attachment.attachmentType,
            noticeExtractionAttachmentId: attachment.attachmentType === 'NOTICE' ? attachment.id : undefined,
            noticeExtractionStatus:
              attachment.attachmentType === 'NOTICE' && !attachment.extracted ? 'RUNNING' : undefined,
            noticeExtractionExtracted:
              attachment.attachmentType === 'NOTICE' ? attachment.extracted === true : undefined,
          },
        },
      }));
    if (notAttachedYet.length) {
      attachExisting(notAttachedYet);
    }
  }, [attachments, files, attachExisting]);

  useEffect(() => {
    const isAttachmentUploadRunning = files.some(
      (file) =>
        file.meta.status !== 'removed' &&
        (file.meta.status === 'queued' ||
          file.meta.status === 'preparing' ||
          file.meta.status === 'uploading' ||
          file.meta.extra?.attachmentRegistering === true),
    );
    if (attachmentUploadRunningRef.current === isAttachmentUploadRunning) {
      return;
    }
    attachmentUploadRunningRef.current = isAttachmentUploadRunning;
    onAttachmentUploadRunningChange(isAttachmentUploadRunning);
  }, [files, onAttachmentUploadRunningChange]);

  useEffect(() => {
    const isNoticeExtractionRunning = files.some(
      (file) =>
        file.meta.status !== 'removed' &&
        (file.meta.extra?.noticeExtractionStatus === 'RUNNING' ||
          file.meta.extra?.noticeExtractionStatus === 'STARTING'),
    );
    if (noticeExtractionRunningRef.current === isNoticeExtractionRunning) {
      return;
    }
    noticeExtractionRunningRef.current = isNoticeExtractionRunning;
    onNoticeExtractionRunningChange(isNoticeExtractionRunning);
  }, [files, onNoticeExtractionRunningChange]);

  useEffect(() => {
    return () => {
      noticeExtractionPollsRef.current.forEach((interval) => window.clearInterval(interval));
      noticeExtractionPollsRef.current.clear();
      noticeExtractionPollStatesRef.current.clear();
      noticeExtractionToastIdsRef.current.forEach((toastId) => toast.dismiss(toastId));
      noticeExtractionToastIdsRef.current.clear();
      noticeExtractionTerminalToastEligibleRef.current.clear();
      pendingNoticeSelectionsRef.current = {};
      onNoticeExtractionRunningChange(false);
      onAttachmentUploadRunningChange(false);
    };
  }, [onAttachmentUploadRunningChange, onNoticeExtractionRunningChange]);

  useEffect(() => {
    files.forEach((uploadFile) => {
      if (uploadFile.meta.extra?.noticeSelectionConfirmed) {
        return;
      }

      const fileSignature = getFileSignature(uploadFile.meta);
      const attachmentType =
        pendingNoticeSelectionsRef.current[fileSignature] ?? pendingNoticeSelectionsRef.current[uploadFile.meta.name];
      if (!attachmentType) {
        return;
      }

      setExtra(uploadFile.meta.id, {
        attachmentType,
        noticeSelectionConfirmed: true,
        ...(attachmentType === 'NOTICE' ? { noticeExtractionStatus: 'STARTING' } : {}),
      });
      delete pendingNoticeSelectionsRef.current[fileSignature];
      delete pendingNoticeSelectionsRef.current[uploadFile.meta.name];
    });
  }, [files, setExtra]);

  useEffect(() => {
    if (!noticeExtractorEnabled || noticeModalFiles.length) {
      return;
    }

    const pendingFiles = files.filter(
      (uploadFile) =>
        uploadFile.meta.status === 'done' &&
        !uploadFile.meta.extra?.noticeSelectionConfirmed &&
        !uploadFile.meta.extra?.noticeSelectionPrompted &&
        !uploadFile.meta.extra?.attachmentRegistered &&
        !uploadFile.meta.extra?.attachmentRegistering,
    );

    const pdfFiles = pendingFiles.filter((uploadFile) => isPdfFileMeta(uploadFile.meta));
    pendingFiles
      .filter((uploadFile) => !isPdfFileMeta(uploadFile.meta))
      .forEach((uploadFile) => {
        setExtra(uploadFile.meta.id, {
          attachmentType: 'GENERAL',
          noticeSelectionConfirmed: true,
        });
      });

    if (!pdfFiles.length) {
      return;
    }

    pdfFiles.forEach((uploadFile) => {
      setExtra(uploadFile.meta.id, { noticeSelectionPrompted: true });
    });
    const modalFiles = pdfFiles.map((uploadFile) => ({
      id: getFileSignature(uploadFile.meta),
      name: uploadFile.meta.name,
      isPdf: true,
    }));
    setNoticeModalFiles(modalFiles);
    setNoticeSelections(
      modalFiles.reduce<Record<string, boolean>>((acc, file) => {
        acc[file.id] = true;
        return acc;
      }, {}),
    );
  }, [files, noticeExtractorEnabled, noticeModalFiles.length, setExtra]);

  const handleBeforeAddFiles = useCallback(async () => {
    const ensuredJobId = await ensureDraftJobBeforeUpload();
    if (!ensuredJobId) {
      throw new Error('Failed to create draft job');
    }
    jobIdRef.current = ensuredJobId;
  }, [ensureDraftJobBeforeUpload, jobIdRef]);

  const handleSelectedFiles = useCallback(() => {
    setNoticeModalFiles([]);
    setNoticeSelections({});
  }, []);

  const confirmNoticeSelections = useCallback(() => {
    const attachmentTypes = noticeModalFiles.reduce<Record<string, TAttachmentType>>((acc, modalFile) => {
      const attachmentType: TAttachmentType = modalFile.isPdf && noticeSelections[modalFile.id] ? 'NOTICE' : 'GENERAL';
      acc[modalFile.id] = attachmentType;
      return acc;
    }, {});

    pendingNoticeSelectionsRef.current = {
      ...pendingNoticeSelectionsRef.current,
      ...attachmentTypes,
    };

    files.forEach((uploadFile) => {
      const fileSignature = getFileSignature(uploadFile.meta);
      const attachmentType = attachmentTypes[fileSignature] ?? attachmentTypes[uploadFile.meta.name];
      if (!attachmentType) {
        return;
      }

      setExtra(uploadFile.meta.id, {
        attachmentType,
        noticeSelectionConfirmed: true,
        ...(attachmentType === 'NOTICE' ? { noticeExtractionStatus: 'STARTING' } : {}),
      });
      delete pendingNoticeSelectionsRef.current[fileSignature];
      delete pendingNoticeSelectionsRef.current[uploadFile.meta.name];
    });
    setNoticeModalFiles([]);
    setNoticeSelections({});
  }, [files, noticeModalFiles, noticeSelections, setExtra]);

  const openNoticeExtraction = useCallback(
    (attachmentId: string) => {
      window.open(
        `${window.location.origin}${routes.noticeExtractor.make(organizationSlug, workspaceSlug)}?workflowId=${encodeURIComponent(attachmentId)}`,
        '_blank',
        'noopener,noreferrer',
      );
    },
    [organizationSlug, workspaceSlug],
  );

  const stopNoticeExtractionPoll = useCallback((fileId: string) => {
    const poll = noticeExtractionPollsRef.current.get(fileId);
    if (poll) {
      window.clearInterval(poll);
      noticeExtractionPollsRef.current.delete(fileId);
    }
    noticeExtractionPollStatesRef.current.delete(fileId);
  }, []);

  const pollNoticeAttachmentExtracted = useCallback(
    (fileId: string, attachmentId: string, fileName: string) => {
      const existingPoll = noticeExtractionPollsRef.current.get(fileId);
      if (existingPoll) {
        return;
      }

      noticeExtractionPollStatesRef.current.set(fileId, {
        consecutiveFailures: 0,
        startedAtMs: Date.now(),
        inFlight: false,
      });

      const finishWithToast = (showToast: (toastId: string) => void) => {
        const toastId = getNoticeExtractionToastId(attachmentId);
        if (noticeExtractionTerminalToastEligibleRef.current.has(attachmentId)) {
          showToast(toastId);
        } else {
          toast.dismiss(toastId);
        }
        noticeExtractionToastIdsRef.current.delete(toastId);
        noticeExtractionTerminalToastEligibleRef.current.delete(attachmentId);
      };

      const refresh = () =>
        runNoticeExtractionPollTick({
          fileId,
          pollStates: noticeExtractionPollStatesRef.current,
          nowMs: () => Date.now(),
          fetchSnapshot: async () => {
            const attachment = await onRefreshAttachment(attachmentId);
            if (!attachment) {
              throw new Error('Attachment refresh returned no data');
            }
            if (attachment.extracted) {
              return { attachment };
            }
            const workflowStatus = await queryClient.fetchQuery({
              queryKey: noticeExtractorKeys.workflowStatus(attachmentId),
              queryFn: createFetchNoticeExtractionStatusQueryFn(call, {
                dontRedirectOnError: true,
                onError: () => undefined,
              }),
            });
            return { attachment, workflowState: workflowStatus?.state };
          },
          onAttachmentRefreshed: onAttachmentChange,
          onContinueRunning: () =>
            setExtra(fileId, {
              noticeExtractionAttachmentId: attachmentId,
              noticeExtractionStatus: 'RUNNING',
              noticeExtractionExtracted: false,
            }),
          onCompleted: () => {
            setExtra(fileId, {
              noticeExtractionAttachmentId: attachmentId,
              noticeExtractionStatus: 'COMPLETED',
              noticeExtractionExtracted: true,
            });
            finishWithToast((toastId) => toast.success(`Notice extraction completed for ${fileName}`, { id: toastId }));
          },
          onFailed: (failure, status) => {
            setExtra(fileId, {
              noticeExtractionAttachmentId: attachmentId,
              noticeExtractionStatus: status,
              noticeExtractionExtracted: false,
            });
            finishWithToast((toastId) =>
              toast.error(getNoticeExtractionFailureMessage(failure, fileName), { id: toastId }),
            );
          },
          stopPoll: () => stopNoticeExtractionPoll(fileId),
        });

      const interval = window.setInterval(() => {
        void refresh();
      }, NOTICE_EXTRACTION_POLL_INTERVAL_MS);
      noticeExtractionPollsRef.current.set(fileId, interval);
      void refresh();
    },
    [call, queryClient, onRefreshAttachment, setExtra, onAttachmentChange, stopNoticeExtractionPoll],
  );

  useEffect(() => {
    files.forEach((file) => {
      const attachmentPath = String(file.meta.extra?.attachmentPath || file.meta.name || '');
      const attachmentId = String(file.meta.extra?.noticeExtractionAttachmentId || '');
      const attachment = attachments.find((item) => item.path === attachmentPath || item.id === attachmentId);

      if (!attachment || attachment.attachmentType !== 'NOTICE' || !attachment.id) {
        return;
      }

      const currentStatus = String(file.meta.extra?.noticeExtractionStatus || '');
      if (noticeExtractionFailedStates.has(currentStatus)) {
        return;
      }

      const localExtracted = file.meta.extra?.noticeExtractionExtracted === true;
      const extracted = localExtracted || attachment.extracted;
      const nextStatus = extracted ? 'COMPLETED' : 'RUNNING';
      if (currentStatus !== nextStatus || file.meta.extra?.noticeExtractionExtracted !== extracted) {
        setExtra(file.meta.id, {
          noticeExtractionAttachmentId: attachment.id,
          noticeExtractionStatus: nextStatus,
          noticeExtractionExtracted: extracted,
        });
      }

      if (!extracted) {
        pollNoticeAttachmentExtracted(file.meta.id, attachment.id, attachment.fileName);
        return;
      }

      stopNoticeExtractionPoll(file.meta.id);

      const toastId = getNoticeExtractionToastId(attachment.id);
      if (noticeExtractionTerminalToastEligibleRef.current.has(attachment.id)) {
        toast.success(`Notice extraction completed for ${attachment.fileName}`, { id: toastId });
      } else {
        toast.dismiss(toastId);
      }
      noticeExtractionToastIdsRef.current.delete(toastId);
      noticeExtractionTerminalToastEligibleRef.current.delete(attachment.id);
    });
  }, [attachments, files, pollNoticeAttachmentExtracted, setExtra, stopNoticeExtractionPoll]);

  useEffect(() => {
    if (!noticeExtractorEnabled) {
      return;
    }

    files.forEach((file) => {
      const attachmentId = String(file.meta.extra?.noticeExtractionAttachmentId || '');
      const attachmentType = file.meta.extra?.attachmentType;
      const status = file.meta.extra?.noticeExtractionStatus;

      if (
        file.meta.status !== 'external' ||
        attachmentType !== 'NOTICE' ||
        !attachmentId ||
        (status !== 'STARTING' && status !== 'RUNNING')
      ) {
        return;
      }

      const toastId = getNoticeExtractionToastId(attachmentId);
      if (
        noticeExtractionTerminalToastEligibleRef.current.has(attachmentId) &&
        !noticeExtractionToastIdsRef.current.has(toastId)
      ) {
        toast.loading(`Notice extraction running for ${file.meta.name}`, {
          id: toastId,
        });
        noticeExtractionToastIdsRef.current.add(toastId);
      }
      pollNoticeAttachmentExtracted(file.meta.id, attachmentId, file.meta.name);
    });
  }, [files, noticeExtractorEnabled, pollNoticeAttachmentExtracted]);

  const startNoticeExtraction = useCallback(
    (fileId: string, attachmentId: string, fileName: string) => {
      if (!noticeExtractorEnabled) {
        return;
      }

      setExtra(fileId, {
        noticeExtractionAttachmentId: attachmentId,
        noticeExtractionStatus: 'RUNNING',
      });
      toast.loading(`Notice extraction running for ${fileName}`, {
        id: getNoticeExtractionToastId(attachmentId),
      });
      noticeExtractionToastIdsRef.current.add(getNoticeExtractionToastId(attachmentId));
      noticeExtractionTerminalToastEligibleRef.current.add(attachmentId);
      pollNoticeAttachmentExtracted(fileId, attachmentId, fileName);
    },
    [noticeExtractorEnabled, pollNoticeAttachmentExtracted, setExtra],
  );

  useEffect(() => {
    files.forEach((f) => {
      if (f.meta.status !== 'done' || f.meta.extra?.attachmentRegistered || f.meta.extra?.attachmentRegistering) {
        return;
      }
      if (noticeExtractorEnabled && !f.meta.extra?.noticeSelectionConfirmed) {
        return;
      }

      setExtra(f.meta.id, { attachmentRegistering: true });
      const attachmentType: TAttachmentType = f.meta.extra?.attachmentType === 'NOTICE' ? 'NOTICE' : 'GENERAL';

      registerAttachment({
        jobId: jobIdRef.current,
        file: f.meta.name,
        oid: organizationId,
        attachmentType,
      })
        .then((response) => {
          if (response) {
            onAttachmentChange(response);
          }
          const uploadedAttachmentPath = response?.filename || f.meta.name;
          setExtra(f.meta.id, {
            attachmentRegistered: true,
            attachmentRegistering: false,
            attachmentPath: uploadedAttachmentPath,
            noticeExtractionAttachmentId: response?.id,
            noticeExtractionExtracted: response?.extracted === true,
          });
          if (attachmentType === 'NOTICE' && response?.id) {
            startNoticeExtraction(f.meta.id, response.id, f.meta.name);
          }
        })
        .catch(() => {
          updateMeta(f.meta.id, {
            status: 'error',
            errorMessage: 'Failed to register attachment on the server',
          });
          setExtra(f.meta.id, { attachmentRegistering: false });
        });
    });
  }, [
    files,
    organizationId,
    noticeExtractorEnabled,
    registerAttachment,
    setExtra,
    updateMeta,
    jobIdRef,
    startNoticeExtraction,
    onAttachmentChange,
  ]);

  const renderNoticeExtractionAction = useCallback(
    (item: (typeof files)[number]) => {
      const attachmentId = String(item.meta.extra?.noticeExtractionAttachmentId || '');
      const status = String(item.meta.extra?.noticeExtractionStatus || '');
      const attachmentType = item.meta.extra?.attachmentType;

      if (!noticeExtractorEnabled || attachmentType !== 'NOTICE') {
        return null;
      }

      if (status === 'COMPLETED' && attachmentId) {
        return (
          <IconButtonRegular
            aria-label={`Open notice extraction for ${item.meta.name}`}
            title="Open notice extraction"
            onClick={() => openNoticeExtraction(attachmentId)}
          >
            <DiagonalArrowIcon className="w-4 h-4" />
          </IconButtonRegular>
        );
      }

      if (noticeExtractionFailedStates.has(status)) {
        return (
          <IconButtonRegular
            aria-label={`Notice extraction failed for ${item.meta.name}`}
            title="Notice extraction failed"
            disabled
          >
            <StatusErrorIcon className="h-4 w-4 text-danger" />
          </IconButtonRegular>
        );
      }

      if (status) {
        return (
          <IconButtonRegular
            aria-label={`Notice extraction running for ${item.meta.name}`}
            title="Extracting notice"
            disabled
          >
            <LoadingIcon className="h-4 w-4 animate-spin" />
          </IconButtonRegular>
        );
      }

      return null;
    },
    [noticeExtractorEnabled, openNoticeExtraction],
  );

  const handleDownload = useCallback(
    async (meta: { name?: string; extra?: Record<string, unknown> }) => {
      const primaryPath = String(meta.extra?.attachmentPath || meta.name || '');
      if (!primaryPath) {
        return;
      }
      const fallbackPath = String(meta.name || '');
      const downloadByPath = async (path: string) =>
        downloadFromUrl(
          `${window.location.origin}/api/media/${jobIdRef.current}/attachments/${encodeURIComponent(path)}`,
          meta.name ?? path,
        );

      try {
        await downloadByPath(primaryPath);
      } catch {
        if (fallbackPath && fallbackPath !== primaryPath) {
          await downloadByPath(fallbackPath).catch(handleDownloadError);
          return;
        }
        handleDownloadError(new Error('Attachment download failed'));
      }
    },
    [jobIdRef],
  );

  const handleRemoveRemote = useCallback(
    async (meta: { name?: string; extra?: Record<string, unknown> }) => {
      const primaryPath = String(meta.extra?.attachmentPath || meta.name || '');
      if (!primaryPath) {
        return;
      }
      const fallbackPath = String(meta.name || '');
      const markRemoved = (path: string) => {
        if (path) {
          removedAttachmentPathsRef.current.add(path);
        }
      };

      try {
        await deleteAttachment({
          oid: organizationId,
          jobId: jobIdRef.current,
          file: primaryPath,
        });
        markRemoved(primaryPath);
        if (fallbackPath && fallbackPath !== primaryPath) {
          markRemoved(fallbackPath);
        }
      } catch {
        if (fallbackPath && fallbackPath !== primaryPath) {
          await deleteAttachment({
            oid: organizationId,
            jobId: jobIdRef.current,
            file: fallbackPath,
          });
          markRemoved(primaryPath);
          markRemoved(fallbackPath);
          return;
        }
        throw new Error('Failed to delete attachment');
      }
    },
    [deleteAttachment, organizationId, jobIdRef],
  );

  const visibleFiles = files.filter((item) => item.meta.status !== 'removed');

  return (
    <div>
      <DropZone
        accept={EACCEPTED_DOCUMENT_FORMATS.join(', ')}
        multiple={true}
        onFiles={handleSelectedFiles}
        onBeforeAddFiles={handleBeforeAddFiles}
      />

      {visibleFiles.map((item) => {
        const erroredState = item.meta.status === 'error';
        const processingState =
          item.meta.status === 'preparing' ||
          item.meta.status === 'uploading' ||
          Boolean(item.meta.extra?.attachmentRegistering);
        const allowRemoveFile = !processingState || erroredState;

        return (
          <div key={item.meta.id} className="mt-2">
            <FilePreview
              item={item}
              allowRemove={allowRemoveFile}
              allowDownload
              actionSlot={renderNoticeExtractionAction(item)}
              onDownload={handleDownload}
              onRemoveRemote={handleRemoveRemote}
            />
          </div>
        );
      })}

      {noticeExtractorEnabled && noticeModalFiles.length ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[color:var(--color-overlay)] px-4">
          <div className="w-full max-w-[520px] rounded-[8px] border border-border bg-surface p-5 shadow-[0_12px_32px_var(--color-shadow)]">
            <h3 className="m-0 text-[16px] font-bold leading-6 text-foreground">Notice attachments</h3>
            <p className="mb-4 mt-2 text-[13px] leading-[18px] text-foreground-muted">
              PDF attachments are selected as notice files by default. Other files cannot be marked as notice.
            </p>
            <div className="grid max-h-[320px] gap-2 overflow-auto pr-1">
              {noticeModalFiles.map((file) => (
                <label
                  className={[
                    'flex items-center gap-3 rounded-[8px] border border-border bg-surface-muted p-3 text-[13px] leading-[18px] text-foreground',
                    file.isPdf ? 'cursor-pointer' : 'cursor-not-allowed opacity-55',
                  ].join(' ')}
                  key={file.id}
                >
                  <CheckBox
                    name={`notice-${file.id}`}
                    checked={file.isPdf && !!noticeSelections[file.id]}
                    disabled={!file.isPdf}
                    onChange={(checked) =>
                      file.isPdf && setNoticeSelections((prev) => ({ ...prev, [file.id]: checked }))
                    }
                  />
                  <span className="min-w-0 flex-1 break-words">{file.name}</span>
                  <span className="text-[12px] font-semibold uppercase text-foreground-muted">
                    {file.isPdf ? 'Notice' : 'Not PDF'}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <OutlinedButton
                onClick={() =>
                  setNoticeSelections(
                    noticeModalFiles.reduce<Record<string, boolean>>((acc, file) => {
                      acc[file.id] = false;
                      return acc;
                    }, {}),
                  )
                }
              >
                Clear all
              </OutlinedButton>
              <Button onClick={confirmNoticeSelections}>Continue</Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const UploadAttachmentsBlock: React.FC<TProps> = ({
  organizationId,
  organizationSlug,
  workspaceSlug,
  noticeExtractorEnabled,
  onNoticeExtractionRunningChange,
  onAttachmentUploadRunningChange,
  attachments,
  ensureDraftJobBeforeUpload,
  getJobId,
  onRefreshAttachment,
  onAttachmentChange,
}) => {
  const { call } = useAPI();
  const jobIdRef = useRef(getJobId());
  jobIdRef.current = getJobId();

  const { mutateAsync: requestUploadLink } = useMutation({
    mutationFn: (request: Parameters<typeof JobsService.getJobAttachmentUploadLink>[0]) =>
      call(() => JobsService.getJobAttachmentUploadLink(request)),
  });

  return (
    <UploadProvider
      config={{
        mode: 'immediate',
        uploader: AttachmentUploader(requestUploadLink, () => jobIdRef.current, organizationId),
        extra: { jobId: jobIdRef.current, organizationId },
      }}
    >
      <UploadAttachmentsBlockInner
        organizationId={organizationId}
        organizationSlug={organizationSlug}
        workspaceSlug={workspaceSlug}
        noticeExtractorEnabled={noticeExtractorEnabled}
        onNoticeExtractionRunningChange={onNoticeExtractionRunningChange}
        onAttachmentUploadRunningChange={onAttachmentUploadRunningChange}
        attachments={attachments}
        ensureDraftJobBeforeUpload={ensureDraftJobBeforeUpload}
        jobIdRef={jobIdRef}
        onRefreshAttachment={onRefreshAttachment}
        onAttachmentChange={onAttachmentChange}
      />
    </UploadProvider>
  );
};
